import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  ViewColumn as ColumnIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSFooter, CMSFooterColumn, CMSFooterLink, CMSFooterSocial } from '../../services';

const CMSFooterPage: React.FC = () => {
  const { t } = useTranslation();
  const [footer, setFooter] = useState<CMSFooter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Main footer editing
  const [mainData, setMainData] = useState({
    descricao_empresa: '',
    copyright_texto: '',
  });

  // Column dialog
  const [columnDialog, setColumnDialog] = useState(false);
  const [columnData, setColumnData] = useState<Partial<CMSFooterColumn> | null>(null);

  // Link dialog
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkData, setLinkData] = useState<Partial<CMSFooterLink> | null>(null);
  const [selectedColumnId, setSelectedColumnId] = useState<number | null>(null);

  // Social dialog
  const [socialDialog, setSocialDialog] = useState(false);
  const [socialData, setSocialData] = useState<Partial<CMSFooterSocial> | null>(null);

  // Delete dialogs
  const [deleteColumnDialog, setDeleteColumnDialog] = useState<{ open: boolean; column: CMSFooterColumn | null }>({
    open: false,
    column: null,
  });
  const [deleteLinkDialog, setDeleteLinkDialog] = useState<{ open: boolean; link: CMSFooterLink | null }>({
    open: false,
    link: null,
  });
  const [deleteSocialDialog, setDeleteSocialDialog] = useState<{ open: boolean; social: CMSFooterSocial | null }>({
    open: false,
    social: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cmsService.getFooter();
      const data = response.data.data;
      setFooter(data);
      if (data) {
        setMainData({
          descricao_empresa: data.descricao_empresa || '',
          copyright_texto: data.copyright_texto || '',
        });
      }
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingFooter'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveMain = async () => {
    setSaving(true);
    setError(null);
    try {
      await cmsService.updateFooter(mainData);
      setSuccess(t('cms.footerUpdated'));
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingFooter'));
    } finally {
      setSaving(false);
    }
  };

  // Column handlers
  const handleOpenColumnEdit = (column?: CMSFooterColumn) => {
    if (column) {
      setColumnData(column);
    } else {
      setColumnData({
        titulo: '',
        ordem: (footer?.colunas?.length || 0) + 1,
      });
    }
    setColumnDialog(true);
  };

  const handleSaveColumn = async () => {
    if (!columnData?.titulo) {
      setError(t('cms.columnTitleRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (columnData.id) {
        await cmsService.updateFooterColumn(columnData.id, {
          titulo: columnData.titulo,
          ordem: columnData.ordem,
        });
        setSuccess(t('cms.columnUpdated'));
      } else {
        await cmsService.createFooterColumn({
          titulo: columnData.titulo,
          ordem: columnData.ordem,
        });
        setSuccess(t('cms.columnCreated'));
      }
      setColumnDialog(false);
      setColumnData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingColumn'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteColumn = async () => {
    if (!deleteColumnDialog.column) return;
    try {
      await cmsService.deleteFooterColumn(deleteColumnDialog.column.id);
      setSuccess(t('cms.columnDeleted'));
      setDeleteColumnDialog({ open: false, column: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingColumn'));
    }
  };

  // Link handlers
  const handleOpenLinkEdit = (columnId: number, link?: CMSFooterLink) => {
    setSelectedColumnId(columnId);
    if (link) {
      setLinkData(link);
    } else {
      setLinkData({
        coluna_id: columnId,
        texto: '',
        url: '',
        badge: '',
        externo: false,
        ordem: 0,
      });
    }
    setLinkDialog(true);
  };

  const handleSaveLink = async () => {
    if (!linkData?.texto || !linkData?.url) {
      setError(t('cms.textUrlRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (linkData.id) {
        await cmsService.updateFooterLink(linkData.id, linkData);
        setSuccess(t('cms.linkUpdated'));
      } else {
        await cmsService.createFooterLink({
          ...linkData,
          coluna_id: selectedColumnId!,
        });
        setSuccess(t('cms.linkCreated'));
      }
      setLinkDialog(false);
      setLinkData(null);
      setSelectedColumnId(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingLink'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = async () => {
    if (!deleteLinkDialog.link) return;
    try {
      await cmsService.deleteFooterLink(deleteLinkDialog.link.id);
      setSuccess(t('cms.linkDeleted'));
      setDeleteLinkDialog({ open: false, link: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingLink'));
    }
  };

  // Social handlers
  const handleOpenSocialEdit = (social?: CMSFooterSocial) => {
    if (social) {
      setSocialData(social);
    } else {
      setSocialData({
        rede: '',
        url: '',
        icone: '',
        ordem: (footer?.social?.length || 0) + 1,
      });
    }
    setSocialDialog(true);
  };

  const handleSaveSocial = async () => {
    if (!socialData?.rede || !socialData?.url) {
      setError(t('cms.socialNetworkUrlRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (socialData.id) {
        await cmsService.updateFooterSocial(socialData.id, socialData);
        setSuccess(t('cms.socialUpdated'));
      } else {
        await cmsService.createFooterSocial(socialData);
        setSuccess(t('cms.socialCreated'));
      }
      setSocialDialog(false);
      setSocialData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingSocial'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSocial = async () => {
    if (!deleteSocialDialog.social) return;
    try {
      await cmsService.deleteFooterSocial(deleteSocialDialog.social.id);
      setSuccess(t('cms.socialDeleted'));
      setDeleteSocialDialog({ open: false, social: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingSocial'));
    }
  };

  const socialOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter / X' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
  ];

  const getSocialIcon = (rede: string) => {
    switch (rede.toLowerCase()) {
      case 'facebook': return <FacebookIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'linkedin': return <LinkedInIcon />;
      case 'youtube': return <YouTubeIcon />;
      default: return <LinkIcon />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.footerTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.footerSubtitle')}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Footer Info */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {t('cms.mainInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('cms.companyDescription')}
                    value={mainData.descricao_empresa}
                    onChange={(e) => setMainData(prev => ({ ...prev, descricao_empresa: e.target.value }))}
                    placeholder={t('cms.companyDescriptionPlaceholder')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    label={t('cms.copyrightText')}
                    value={mainData.copyright_texto}
                    onChange={(e) => setMainData(prev => ({ ...prev, copyright_texto: e.target.value }))}
                    placeholder={t('cms.copyrightPlaceholder')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveMain}
                    disabled={saving}
                    sx={{ height: '56px' }}
                  >
                    {saving ? t('common.saving') : t('cms.saveChanges')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Columns */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {t('cms.linkColumns')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenColumnEdit()}
                >
                  {t('cms.newColumn')}
                </Button>
              </Box>

              {!footer?.colunas || footer.colunas.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <ColumnIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {t('cms.noColumnsCreated')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenColumnEdit()}
                    sx={{ mt: 2 }}
                  >
                    {t('cms.createFirstColumn')}
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {footer.colunas.map((column) => (
                    <Grid key={column.id} size={{ xs: 12, md: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {column.titulo}
                          </Typography>
                          <Box>
                            <Tooltip title={t('cms.editColumn')}>
                              <IconButton size="small" onClick={() => handleOpenColumnEdit(column)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('cms.deleteColumn')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteColumnDialog({ open: true, column })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <List dense>
                          {column.links && column.links.length > 0 ? (
                            column.links.map((link, index) => (
                              <React.Fragment key={link.id}>
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="center" gap={1}>
                                        {link.texto}
                                        {link.badge && (
                                          <Chip size="small" label={link.badge} color="primary" />
                                        )}
                                        {link.externo && (
                                          <Chip size="small" label={t('cms.external')} variant="outlined" />
                                        )}
                                      </Box>
                                    }
                                    secondary={link.url}
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenLinkEdit(column.id, link)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDeleteLinkDialog({ open: true, link })}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                                {index < (column.links?.length || 0) - 1 && <Divider />}
                              </React.Fragment>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText
                                secondary={t('cms.noLinksInColumn')}
                                sx={{ textAlign: 'center' }}
                              />
                            </ListItem>
                          )}
                        </List>

                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenLinkEdit(column.id)}
                          sx={{ mt: 1 }}
                        >
                          {t('cms.addLink')}
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Social Media */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {t('cms.socialMedia')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSocialEdit()}
                >
                  {t('cms.addSocialMedia')}
                </Button>
              </Box>

              {!footer?.social || footer.social.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <Typography color="text.secondary">
                    {t('cms.noSocialConfigured')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSocialEdit()}
                    sx={{ mt: 2 }}
                  >
                    {t('cms.addFirstSocial')}
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {footer.social.map((social) => (
                    <Grid key={social.id} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'primary.main',
                            }}
                          >
                            {getSocialIcon(social.rede)}
                          </Box>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                              {social.rede}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {social.url}
                            </Typography>
                          </Box>
                          <Box>
                            <Tooltip title={t('common.edit')}>
                              <IconButton size="small" onClick={() => handleOpenSocialEdit(social)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteSocialDialog({ open: true, social })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Column Dialog */}
      <Dialog open={columnDialog} onClose={() => setColumnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {columnData?.id ? t('cms.editColumn') : t('cms.newColumn')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.columnTitle')}
                value={columnData?.titulo || ''}
                onChange={(e) => setColumnData(prev => ({ ...prev, titulo: e.target.value }))}
                required
                placeholder={t('cms.columnTitlePlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={columnData?.ordem || 0}
                onChange={(e) => setColumnData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                helperText={t('cms.orderHelperText')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveColumn}
            variant="contained"
            disabled={saving || !columnData?.titulo}
          >
            {saving ? t('common.saving') : columnData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {linkData?.id ? t('cms.editLink') : t('cms.newLink')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.linkTextLabel')}
                value={linkData?.texto || ''}
                onChange={(e) => setLinkData(prev => ({ ...prev, texto: e.target.value }))}
                required
                placeholder={t('cms.linkTextLinkPlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="URL"
                value={linkData?.url || ''}
                onChange={(e) => setLinkData(prev => ({ ...prev, url: e.target.value }))}
                required
                placeholder={t('cms.urlPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.badgeOptional')}
                value={linkData?.badge || ''}
                onChange={(e) => setLinkData(prev => ({ ...prev, badge: e.target.value }))}
                placeholder={t('cms.badgePlaceholder')}
                helperText={t('cms.badgeHelperText')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={linkData?.ordem || 0}
                onChange={(e) => setLinkData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={linkData?.externo || false}
                    onChange={(e) => setLinkData(prev => ({ ...prev, externo: e.target.checked }))}
                  />
                }
                label={t('cms.externalLink')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveLink}
            variant="contained"
            disabled={saving || !linkData?.texto || !linkData?.url}
          >
            {saving ? t('common.saving') : linkData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Column Dialog */}
      <Dialog
        open={deleteColumnDialog.open}
        onClose={() => setDeleteColumnDialog({ open: false, column: null })}
      >
        <DialogTitle>{t('cms.deleteColumn')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteColumn', { name: deleteColumnDialog.column?.titulo })}
          </Typography>
          <Typography variant="body2" color="error" mt={1}>
            {t('cms.columnDeleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteColumnDialog({ open: false, column: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteColumn} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Link Dialog */}
      <Dialog
        open={deleteLinkDialog.open}
        onClose={() => setDeleteLinkDialog({ open: false, link: null })}
      >
        <DialogTitle>{t('cms.deleteLink')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteLink', { name: deleteLinkDialog.link?.texto })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLinkDialog({ open: false, link: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteLink} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Dialog */}
      <Dialog open={socialDialog} onClose={() => setSocialDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {socialData?.id ? t('cms.editSocial') : t('cms.newSocial')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                select
                label={t('cms.socialNetwork')}
                value={socialData?.rede || ''}
                onChange={(e) => setSocialData(prev => ({ ...prev, rede: e.target.value }))}
                required
                SelectProps={{ native: true }}
              >
                <option value="">{t('cms.selectOption')}</option>
                {socialOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.profileUrl')}
                value={socialData?.url || ''}
                onChange={(e) => setSocialData(prev => ({ ...prev, url: e.target.value }))}
                required
                placeholder={t('cms.profileUrlPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.iconOptional')}
                value={socialData?.icone || ''}
                onChange={(e) => setSocialData(prev => ({ ...prev, icone: e.target.value }))}
                placeholder={t('cms.iconPlaceholder')}
                helperText={t('cms.iconHelperText')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={socialData?.ordem || 0}
                onChange={(e) => setSocialData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSocialDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveSocial}
            variant="contained"
            disabled={saving || !socialData?.rede || !socialData?.url}
          >
            {saving ? t('common.saving') : socialData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Social Dialog */}
      <Dialog
        open={deleteSocialDialog.open}
        onClose={() => setDeleteSocialDialog({ open: false, social: null })}
      >
        <DialogTitle>{t('cms.deleteSocial')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteSocial', { name: deleteSocialDialog.social?.rede })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSocialDialog({ open: false, social: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteSocial} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSFooterPage;
